import {
  AccountAttributeTileType,
  AccountCopyAttributeTileType,
  AccountDetailsLinkTileType,
  AccountToggleAttributeTileType,
  AccountUpDownAttributeTileType,
  AddMoneyTileType,
  ContentCtaTileType,
  EmptyStateContentTileType,
  IInternalAccount,
  InternalAccountPageTileInput,
  ITile,
  ListTileType,
  NotificationBannerTileType,
  PillButtonRowComponent,
  Query,
  QuickActionTileType,
  TileType,
} from '@varo/graphql-schema-types';
import { useFocusEffect } from 'expo-router';
import { useCallback, useEffect, useState } from 'react';
import AccountPageTileQuery from 'services/graphql/queries/AccountPageTile';
import { CombinedError, useQuery, UseQueryExecute } from 'urql';

interface AccountSummaryResult {
  accountR2: IInternalAccount | null | undefined;
  navigationTab: PillButtonRowComponent | null | undefined; // the pill component from smartView tabs
  tiles: ITile[] | undefined; // the list of the account tiles to be displayed
  isLoading: boolean;
  error?: CombinedError;
  refreshAccountSummary: UseQueryExecute;
  staleAccountSummary: boolean;
  isInitialLoad: boolean;
}

const useGetAccountSummary = (id: string): AccountSummaryResult => {
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const accountPageTileInput = {
    accountId: id,
    tileType: [
      TileType.List,
      TileType.InternalAccountPageCheckings,
      TileType.InternalAccountPageSavings,
      TileType.InternalAccountPageChargeCard,
      TileType.DetailsLink,
      TileType.QuickAction,
      TileType.EmptyStateContent,
      TileType.ContentCta,
      TileType.AccountAttribute,
      TileType.ReferralBanner,
      TileType.NotificationBanner,
      TileType.AddMoney,
    ],
    listTileType: [
      ListTileType.QuickActions,
      ListTileType.AccountAttributes,
      ListTileType.Banners,
      ListTileType.ContentCtaTiles,
      ListTileType.ChargeCardAccountCarousel,
      ListTileType.AccountActionsTiles,
      ListTileType.AccountActionsTilesChargeCard,
    ],
    quickActionTileType: [
      QuickActionTileType.AddMoney,
      QuickActionTileType.Transfer,
      QuickActionTileType.PayCard,
      QuickActionTileType.ChargeCardViewCard,
      QuickActionTileType.CheckingViewCard,
    ],
    accountAttributeTileType: [AccountAttributeTileType.CardBalance],
    addMoneyTileType: [AddMoneyTileType.CheckingEmptyAccount],
    accountCopyAttributeTileType: [
      AccountCopyAttributeTileType.InterestEarned,
      AccountCopyAttributeTileType.CurrentApy,
      AccountCopyAttributeTileType.SafePay,
      AccountCopyAttributeTileType.ActivateCard,
      AccountCopyAttributeTileType.ChargeCardMoveYourPay,
    ],
    accountToggleAttributeTileType: [
      AccountToggleAttributeTileType.AutoSaving,
      AccountToggleAttributeTileType.SafePay,
      AccountToggleAttributeTileType.ChargeCardMoveYourPay,
    ],
    accountUpDownAttributeTileType: [
      AccountUpDownAttributeTileType.CreditScore,
    ],
    notificationBannerTileType: [
      NotificationBannerTileType.PastDue,
      NotificationBannerTileType.InsufficientFunds,
      NotificationBannerTileType.OneTimePaymentFailed,
      NotificationBannerTileType.UpcomingPayment,
      NotificationBannerTileType.AutoPayFailed,
      NotificationBannerTileType.PaymentDue,
    ],
    accountDetailsLinkTileType: [
      AccountDetailsLinkTileType.CheckingEmptyAccount,
      AccountDetailsLinkTileType.SavingsEmptyAccount,
      AccountDetailsLinkTileType.ChargeCardEmptyAccount,
    ],
    emptyStateContentTileType: [
      EmptyStateContentTileType.CheckingEmptyAccount,
      EmptyStateContentTileType.SavingsEmptyAccount,
      EmptyStateContentTileType.ChargeCardEmptyAccount,
    ],
    contentCtaTileType: [ContentCtaTileType.ProgressTile],
  } as InternalAccountPageTileInput;
  const [
    { data, fetching, error, stale: staleAccountSummary },
    refreshAccountSummary,
  ] = useQuery<Query>({
    query: AccountPageTileQuery,
    variables: { input: accountPageTileInput, id: id },
    requestPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (!fetching) {
      // After the initial load, stop showing the loader
      setIsInitialLoad(false);
    }
  }, [fetching]);

  useFocusEffect(
    useCallback(() => {
      refreshAccountSummary({ requestPolicy: 'network-only' });
    }, [refreshAccountSummary]),
  );

  return {
    accountR2: data?.accountR2,
    navigationTab: data?.accountPageTile.navigation, // the navigation pills component
    tiles: data?.accountPageTile.tiles, // tiles of the account to be displayed
    isLoading: fetching,
    error,
    refreshAccountSummary,
    staleAccountSummary,
    isInitialLoad,
  };
};

export default useGetAccountSummary;
